import React, { useLayoutEffect, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, change } from 'redux-form';
import { Form, FormGroup, FormText } from 'reactstrap';
import {
  clearItemToItinerary,
  getCargoItemsByLocationId,
  getShelfByLocation,
  getUserAgencies,
} from '../../../../actions';
import { isRequired } from '../../../../utils/validators';
import { optionsPropTypes } from '../../../common/forms/select/SelectPropTypes';
import Select from '../../../common/forms/select/Select';
import FormItem from '../../../common/forms/FormItem';
import RelocationCargoItemResult from './RelocationCargoItemResult';
import { NO_SHELF_CARGO_ITEM_SELECTED } from '../../../../config/messages';
import Alert from '../../../common/informative/Alert';

export const RelocationCargoItemForm = ({
  user,
  handleSubmit,
  agencyOptions,
  loadingAgencies,
  shelfOptions,
  loadingShelfOptions,
  dispatchGetUserAgencies,
  dispatchClearItemToItinerary,
  dispatchChange,
  dispatchGetCargoItemsByLocationId,
  dispatchGetShelfByLocationId,
}) => {
  const didDispatchAgency = useRef(false);

  const [locationIdSelected, setLocationIdSelected] = useState(null);
  const [location, setLocation] = useState(null);

  useLayoutEffect(
    () => () => {
      dispatchClearItemToItinerary();
    },
    [],
  );

  useEffect(() => {
    dispatchGetUserAgencies({ userId: user.id });
  }, [user]);

  useEffect(() => {
    if (
      agencyOptions &&
      agencyOptions.length > 0 &&
      !didDispatchAgency.current
    ) {
      dispatchChange(
        'RelocationCargoItemForm',
        'sourceAgency',
        agencyOptions[0],
      );

      dispatchGetShelfByLocationId(agencyOptions[0].value);

      didDispatchAgency.current = true;

      setLocationIdSelected(agencyOptions[0].value);
      setLocation(agencyOptions[0]);
      dispatchGetCargoItemsByLocationId({ locationId: agencyOptions[0].value });
    }
  }, [agencyOptions]);

  const onHandleSubmit = ({ sourceAgency, shelf }) => {
    const sourceLocationId = sourceAgency.value;

    const payload = {
      locationId: sourceLocationId,
    };

    if (shelf) {
      payload.shelfId = shelf.value;
    }

    dispatchGetCargoItemsByLocationId(payload);
    setLocationIdSelected(sourceLocationId);
    setLocation(sourceAgency);
  };

  const handleChangeSourceAgency = (option) => {
    dispatchChange('RelocationCargoItemForm', 'shelf', null);
    if (!option) return;
    const { value } = option;
    dispatchGetShelfByLocationId(value);
    dispatchGetCargoItemsByLocationId({ locationId: value });
    setLocationIdSelected(value);
    setLocation(option);
  };

  const handleChangeShelf = (option) => {
    if (!option && locationIdSelected === null) {
      return;
    }

    if (!option) {
      dispatchGetCargoItemsByLocationId({
        locationId: locationIdSelected,
      });
      return;
    }

    const { value } = option;
    dispatchGetCargoItemsByLocationId({
      shelfId: value,
      locationId: locationIdSelected,
    });
  };

  const onHandleClear = () => {
    dispatchChange('RelocationCargoItemForm', 'shelf', null);
    dispatchGetCargoItemsByLocationId({ locationId: locationIdSelected });
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        <FormGroup row>
          <FormItem label="Agencia Actual" required>
            <Field
              name="sourceAgency"
              component={Select}
              isLoading={loadingAgencies}
              options={agencyOptions}
              isDisabled={agencyOptions.length <= 1}
              validate={[isRequired]}
              onValueChange={handleChangeSourceAgency}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Anaquel">
            <Field
              name="shelf"
              component={Select}
              isLoading={loadingShelfOptions}
              isDisabled={loadingShelfOptions || shelfOptions.length === 0}
              options={shelfOptions}
              placeholder="Seleccione anaquel"
              onValueChange={handleChangeShelf}
              isClearable
            />
            <FormText>Este campo es opcional</FormText>
          </FormItem>
        </FormGroup>
      </Form>

      {locationIdSelected == null && (
        <Alert type="info" message={NO_SHELF_CARGO_ITEM_SELECTED} />
      )}
      {locationIdSelected && (
        <RelocationCargoItemResult
          locationId={locationIdSelected}
          location={location}
          onHandleClear={onHandleClear}
        />
      )}
    </>
  );
};

RelocationCargoItemForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    customer: PropTypes.shape({
      fullName: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  loadingAgencies: PropTypes.bool,
  agencyOptions: optionsPropTypes,
  dispatchChange: PropTypes.func.isRequired,
  dispatchGetUserAgencies: PropTypes.func.isRequired,
  dispatchClearItemToItinerary: PropTypes.func.isRequired,
  dispatchGetCargoItemsByLocationId: PropTypes.func.isRequired,
  shelfOptions: optionsPropTypes.isRequired,
  loadingShelfOptions: PropTypes.bool.isRequired,
  dispatchGetShelfByLocationId: PropTypes.func.isRequired,
};

RelocationCargoItemForm.defaultProps = {
  loadingAgencies: false,
  agencyOptions: [],
};

const mapStateToProps = (state) => ({
  user: state.authentication.get('user') || undefined,
  loadingAgencies: state.HumanResourcesUnit.Agency.getIn(['all', 'loading']),
  agencyOptions: state.HumanResourcesUnit.Agency.getIn([
    'all',
    'content',
    'content',
  ]).map((agency) => ({
    value: agency.locationId,
    label: agency.name,
  })),
  shelfOptions: state.CargoUnit.Shelf.getIn(['all', 'content', 'content']).map(
    (option) => ({
      value: option.id,
      label: option.name,
    }),
  ),
  loadingShelfOptions: state.CargoUnit.Shelf.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchGetUserAgencies: getUserAgencies,
  dispatchClearItemToItinerary: clearItemToItinerary,
  dispatchGetCargoItemsByLocationId: getCargoItemsByLocationId,
  dispatchGetShelfByLocationId: getShelfByLocation,
};

const formComponent = reduxForm({
  form: 'RelocationCargoItemForm',
})(RelocationCargoItemForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
